import React, {Component, ReactElement} from 'react'
import AdminAuthRepository from './repository/AdminAuthRepository'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {AdminUserLoginInfo} from './repository/AdminUserLoginInfo'
import {Box, Divider} from '@mui/material';
import PPTextField from '../../../components/shared/forms/PPTextField'
import styled from 'styled-components'
import PPCheckboxGroup from '../../../components/shared/forms/PPCheckboxGroup'
import {AVAILABLE_SITE, DO_OR_NOT, MESSAGE_TYPE, VALID_OR_NOT_CHECK, YES_OR_NOT} from '../../../CodeMaster'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {Messages} from '../../../Messages'
import {NatGetMenuMasterAuthedAdmin} from '../menuMaster/repository/NatGetMenuMasterAuthedAdmin'
import NatMenuMasterRepo from '../menuMaster/repository/NatMenuMasterRepo'
import {Nullable} from '../../../app/TypeHelpers'
import {
    HeadingAreaMessageComponent,
    HeadingAreaMessageParams,
} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {getQueryParameter} from '../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatSessionRepo, {PostAdminCondominiumSelectParams} from '../session/repository/NatSessionRepo'
import {NatPostAdminCondominiumSelect} from '../session/repository/NatPostAdminCondominiumSelect'
import {InlineLink, StyledLink} from '../../../styledComponents/adminPage/Links'
import {NatAdminLoginInfoSessionStorageHelper} from '../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {Colors, FontWeights} from '../../../styledComponents/Constants'
import {CookieHelper} from '../../../CookieHelper'
import {StyledPre} from '../../../styledComponents/adminPage/StyledPre'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {FlexGridArea} from '../../../styledComponents/GridComponents'
import {AppAdminContext} from '../../../app/AppAdminContext'
import {Location} from 'react-router-dom'

interface Props {
    adminAuthRepo: AdminAuthRepository
    menuMasterRepo: NatMenuMasterRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location
    sessionRepo: NatSessionRepo
}

interface State {
    id: string
    password: string
    keepLoginId: string
    errorMessage: string
}

const KEY_LOGIN_ID: string = 'adminLoginId'


class NatAdminLoginPage extends Component<Props & AdminPagePropsSet, State> {
    declare context: React.ContextType<typeof AppAdminContext>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        const {NOT} = VALID_OR_NOT_CHECK
        this.state = {
            id: '',
            password: '',
            keepLoginId: NOT.value,
            errorMessage: '',
        }

        this.handleOkButtonClick = this.handleOkButtonClick.bind(this)
        this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this)
        this.handleLoginButtonClick = this.handleLoginButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {

        // ログアウトボタンを押下せずにログイン画面へ遷移した場合はログアウト処理を行う。
        if (sessionStorage.length > 0) {
            await this.logoutProcedure()
        }

        // メンテナンスモード判定用に、現在日時取得APIを実行
        await this.props.asyncCallFunction(
            this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
            (httpErrorOrNetworkError) => {
                this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )

        // cookieの社員ID設定
        const adminLoginId = CookieHelper.getStringValue(KEY_LOGIN_ID)
        if (adminLoginId) {
            const {VALID} = VALID_OR_NOT_CHECK
            this.setState({
                id: adminLoginId,
                keepLoginId: VALID.value,
            })
        }
        this.showInitialMessage()
    }

    async logoutProcedure(): Promise<void> {
        // NOTE: 機会があるときにAdminSessionUtilのログアウト処理に統合する
        await this.props.eventEmitter.showLoading()
        try {
            await this.props.asyncCallFunction(
                this.props.adminAuthRepo.logout.bind(this.props.adminAuthRepo),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            await this.props.asyncCallFunction(
                this.props.sessionRepo.postAdminUseSite.bind(this.props.sessionRepo),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            SessionStorageHelper.removeAllItem()
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    showInitialMessage(): void {
        const sessionTimeOut: string = getQueryParameter('sessionTimeOut', window.location.href)
        let initialMessage: Nullable<HeadingAreaMessageParams> = null

        if (sessionTimeOut === DO_OR_NOT.DO.value) {
            initialMessage = {
                title: null,
                messageLevel: MESSAGE_TYPE.WARNING,
                detail: Messages.ESYCC0021.toString(),
            }
        } else if (this.props.location && this.props.location.state) {
            initialMessage = this.props.location.state.initialMessage
            this.props.navigate(this.props.location.pathname, {replace: true})
        } else {
            return
        }

        if (initialMessage) {
            this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
        }
    }

    async handleOkButtonClick(isAdLogin: boolean): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.logoutProcedure()
        if (isAdLogin) {
            this.adLogin()
        } else {
            await this.login()
        }
    }

    handleCancelButtonClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleLoginButtonClick(isAdLogin: boolean): Promise<void> {
        const response = await this.props.asyncCallFunction(
            this.props.sessionRepo.getLoginStatus.bind(this.props.sessionRepo),
            (httpErrorOrNetworkError) => {
                this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        if (response === null) {
            return
        }

        if (response === AVAILABLE_SITE.NOT_LOGIN.value) {
            if (isAdLogin) {
                await this.adLogin()
            } else {
                await this.login()
            }
        } else {
            let errorLevel: MESSAGE_TYPE
            let errorMessage: string
            if (response === AVAILABLE_SITE.RESIDENT.value) {
                errorLevel = Messages.WOPCA0005.errorLevel
                errorMessage = Messages.WOPCA0005.toString()
            } else if (response === AVAILABLE_SITE.ADMIN.value) {
                errorLevel = Messages.WOPCA0006.errorLevel
                errorMessage = Messages.WOPCA0006.toString()
            } else {
                errorLevel = Messages.WOPCA0007.errorLevel
                errorMessage = Messages.WOPCA0007.toString()
            }

            await this.props.eventEmitter.showDialog({
                    title: '確認',
                    errorLevel: errorLevel,
                    message: errorMessage,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleCancelButtonClick,
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: () => this.handleOkButtonClick(isAdLogin),
                        },
                    ],
                },
            )
        }
    }

    async login(): Promise<void> {
        await this.props.eventEmitter.clearHeadingAreaMessage()
        await this.props.eventEmitter.showLoading()

        const now: Date = new Date()
        now.setFullYear(now.getFullYear() + 3)

        // ログイン認証の結果をリセット
        this.setState({errorMessage: ''})

        // ログインID省略
        const {VALID} = VALID_OR_NOT_CHECK
        if (this.state.keepLoginId === VALID.value) {
            CookieHelper.setStringValue(KEY_LOGIN_ID, this.state.id)
        } else {
            CookieHelper.clearValue(KEY_LOGIN_ID)
        }

        // ログイン認証
        try {
            // メンテナンスモード判定用に、何かしらのAPIを実行
            await this.props.asyncCallFunction(
                this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            const loginInfo: AdminUserLoginInfo = await this.props.adminAuthRepo.login(
                this.state.id, this.state.password)

            if (loginInfo.errorInfo && loginInfo.errorInfo.detailMessage) {
                this.setState({
                    errorMessage: loginInfo.errorInfo.detailMessage,
                })
                return
            }

            // 認証情報をsession storageに保持
            NatAdminLoginInfoSessionStorageHelper.setSessionStorage(loginInfo)

            const selectedCondominiumCd = getQueryParameter('condominiumCd', this.props.location.search)
            if (selectedCondominiumCd) {
                // CSRFトークンを設定するために、GETのAPIを実行
                await this.props.asyncCallFunction(
                    this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

                const request: PostAdminCondominiumSelectParams = {
                    condominiumCd: selectedCondominiumCd,
                }
                const response: Nullable<NatPostAdminCondominiumSelect> =
                    await this.props.asyncCallFunction(
                        this.props.sessionRepo.postAdminCondominiumSelect.bind(this.props.sessionRepo, request),
                        async (httpErrorOrNetworkError) => {
                            await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                        },
                    )
                if (!response) {
                    return
                }
                NatAdminLoginInfoSessionStorageHelper.setSessionStorageForCondominiumMode(
                    response.authority,
                    selectedCondominiumCd,
                    response.condominiumName,
                )
            }

            let nextUrl: {
                pathname?: string,
                search?: string,
            } = {}
            const seniUrl: string = getQueryParameter('seniurl', this.props.location.search)
            if (seniUrl) {
                nextUrl = {
                    pathname: seniUrl.split('?')[0],
                    search: seniUrl.split('?')[1],
                }
            }
            if (!nextUrl.pathname) {
                // 一番左（最初）のメニューを取得
                const menuInfo: Nullable<NatGetMenuMasterAuthedAdmin> = await this.props.asyncCallFunction(
                    this.props.menuMasterRepo.getMenuMasterAuthedAdmin.bind(this.props.menuMasterRepo),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (menuInfo === null) {
                    return
                }

                nextUrl.pathname = menuInfo.details?.[0]?.childMenuInfo?.[0]?.url
                if (!nextUrl.pathname) {
                    // メニューが取得できない場合はエラー表示する
                    const headingAreaMessageParams: HeadingAreaMessageParams = {
                        messageLevel: MESSAGE_TYPE.ERROR,
                        title: null,
                        detail: '物件に利用できる機能がないため、ログインできませんでした。',
                    }
                    await this.props.eventEmitter.showHeadingAreaMessage(headingAreaMessageParams)
                    await this.logoutProcedure()
                    return
                }
            }

            const {YES} = YES_OR_NOT
            if (YES.value === loginInfo.passwordUnstrengthFlag) {
                // パスワードが未強化の場合は、パスワード変更画面へ遷移
                // この際、パスワード変更後に遷移する画面を引き継ぐ
                this.props.navigate(
                    '/admin/changePassword',
                    {state: {nextUrl: nextUrl}},
                )
            } else {
                // パスワード強化済の場合は、メニュー左端の画面へ遷移
                this.props.navigate(nextUrl)
            }

        } catch (e) {
            this.setState({errorMessage: Messages.ESYCC0015.message})
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleIdInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
        // @ts-ignore
        this.setState({
            id: event.target.value,
        })
    }

    handlePasswordInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
        // @ts-ignore
        this.setState({
            password: event.target.value,
        })
    }

    handleKeepLoginIdChange(): void {
        if (this.state.keepLoginId === VALID_OR_NOT_CHECK.VALID.value) {
            this.setState({
                keepLoginId: VALID_OR_NOT_CHECK.NOT.value,
            })
        } else {
            this.setState({
                keepLoginId: VALID_OR_NOT_CHECK.VALID.value,
            })
        }
    }

    adLogin(): void {
        let queryParam = window.location.search.slice(1)
        if (queryParam !== '') {
            queryParam = 'useSite=1&' + queryParam
        } else {
            queryParam = 'useSite=1'
        }
        window.location.href = `/common/adSso/reception?` + queryParam
    }

    render(): ReactElement {
        const labels: { label: string, checked: boolean }[] = [{
            label: '次回から社員IDの入力を省略する',
            checked: this.state.keepLoginId === VALID_OR_NOT_CHECK.VALID.value,
        }]

        const loginButtonDisabled = this.state.id === '' || this.state.password === ''

        const spMode = this.context.showSpModePage

        return (
            <MainContainer>
                <Box height={spMode ? '9px' : '25px'}/>
                <HeaderArea $spMode={spMode}>
                    <HeaderIconImage src={'/resources/images/print/haseko_icon.svg'} $spMode={spMode}/>
                    <HeaderTitle>長谷工グループの方</HeaderTitle>
                </HeaderArea>
                <StyledDivider/>
                <HeadingAreaMessageComponent eventEmitter={this.props.eventEmitter}/>
                <InfoMessageArea>
                    管理組合役員の方は、<InlineLink href={'/admin/assoc/login'}>管理組合用ログイン画面</InlineLink>からログインしてください。
                </InfoMessageArea>
                <MainArea id={'login-form'} $spMode={spMode}>
                    <FlexGridArea area={'loginIdLabel'}>
                        <RowTitle>
                            社員ID
                        </RowTitle>
                    </FlexGridArea>
                    <FlexGridArea area={'loginIdInput'}>
                        <LoginIdInput
                            $spMode={spMode}
                            InputProps={{
                                inputProps: {
                                    maxLength: 10,
                                },
                            }}
                            required
                            value={this.state.id}
                            onChange={this.handleIdInputChange.bind(this)}
                            autoFocus
                        />
                    </FlexGridArea>
                    <FlexGridArea area={'keepLoginIdCheck'}>
                        <StyledPPCheckboxGroup
                            data={labels}
                            onChangeChecked={this.handleKeepLoginIdChange.bind(this)}
                        />
                    </FlexGridArea>
                    <FlexGridArea area={'passwordLabel'}>
                        <RowTitle>
                            パスワード
                        </RowTitle>
                    </FlexGridArea>
                    <FlexGridArea area={'passwordInput'}>
                        <PasswordInput
                            $spMode={spMode}
                            type="password"
                            inputProps={{
                                maxLength: 20,
                            }}
                            onChange={this.handlePasswordInputChange.bind(this)}
                        />
                    </FlexGridArea>
                    <FlexGridArea area={'errorMessage'}>
                        {this.state.errorMessage !== '' &&
                            <ErrorMsg>{this.state.errorMessage}</ErrorMsg>}
                    </FlexGridArea>
                    <FlexGridArea area={'adLoginLink'} justifyContent={spMode ? 'center' : 'normal'}>
                        <StyledLink
                            onClick={() => this.handleLoginButtonClick(true)}
                        >
                            長谷工グループ社員の方はこちら
                        </StyledLink>
                    </FlexGridArea>
                    <FlexGridArea area={'loginButton'}>
                        <LoginButton
                            $spMode={spMode}
                            disabled={loginButtonDisabled}
                            onClick={() => this.handleLoginButtonClick(false)}
                        >
                            ログイン
                        </LoginButton>
                    </FlexGridArea>
                </MainArea>
            </MainContainer>
        )
    }
}

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const HeaderArea = styled.div<{ $spMode: boolean }>`
  display: flex;
  flex-direction: row;
  font-weight: ${FontWeights.Bold};
  align-items: center;
  ${props => props.$spMode ? `
     font-size: 16px;
  ` : `
     font-size: 24px;
  `}
`

const HeaderIconImage = styled.img<{ $spMode: boolean }>`
  ${props => props.$spMode ? `
     width: 20px;
     height: 20px;
  ` : `
     width: 24px;
     height: 24px;
  `}
`

const HeaderTitle = styled.div`
  margin-left: 4px
`

const StyledDivider = styled(Divider)`
  margin: 4px 0 16px 0;
  height: 2px;
  background: linear-gradient(0deg, rgba(29, 37, 84, 0.20) 0%, rgba(29, 37, 84, 0.20) 100%), #FFF;
  filter: drop-shadow(1px 1px 1px rgba(29, 37, 84, 0.40));
`

const InfoMessageArea = styled.div`
  padding: 8px;
  background-color: #E5E5E5;
`

const MainArea = styled.div<{ $spMode: boolean }>`
  background-color: ${Colors.Surface};
  display: grid;
  ${props => props.$spMode ? `
  margin: 16px -15px;
  padding: 16px;
  grid-template:
    "loginIdLabel"
    "."                5px
    "loginIdInput"
    "keepLoginIdCheck"
    "."                15px
    "passwordLabel"
    "."                5px
    "passwordInput"
    "errorMessage"
    "."                32px
    "loginButton"
    "."                32px
    "adLoginLink"
    "."                16px
     / 1fr;
  ` : `
  margin-top: 16px;
  padding: 20px 40px 24px;
  grid-template:
    "loginIdLabel  loginIdInput     ."
    ".             keepLoginIdCheck ."
    ".             .                ." 28px
    "passwordLabel passwordInput    ."
    "errorMessage  errorMessage     ."
    ".             .                ." 24px
    "adLoginLink   adLoginLink      loginButton"
     / max-content 1fr max-content;
  column-gap: 40px;
  `}
`

const RowTitle = styled.div`
  font-size: 16px;
  color: ${Colors.AccentDark};
`

const LoginIdInput = styled(PPTextField)<{ $spMode: boolean }>`
  ${props => props.$spMode ? `
  width: 100%;
  ` : `
  width: 240px;
  `}
`

const PasswordInput = styled(PPTextField)<{ $spMode: boolean }>`
  ${props => props.$spMode ? `
  width: 100%;
  ` : `
  width: 480px;
  `}
`

const StyledPPCheckboxGroup = styled(PPCheckboxGroup)`
  .MuiCheckbox-root {
    padding: 4px;
    margin-left: 7px;
  }

  .MuiTypography-root {
    font-size: 14px;
    color: ${Colors.AccentDark};
  }
`

const ErrorMsg = styled(StyledPre)`
  margin-top: 16px;
  color: ${Colors.Alert};
`

const LoginButton = styled(AdminPrimaryButton)<{ $spMode: boolean }>`
  ${props => props.$spMode && `
  width: 100%;
  `}
`

NatAdminLoginPage.contextType = AppAdminContext
export default createAdminPage(NatAdminLoginPage)
